// /admin/*: only for staff, and only after 2-step login (aal2, design 4.18). A staff member at aal1
// enrols an authenticator app, or answers its challenge, through supabase.auth.mfa, inside admin's own
// frame. Anyone without a staff role gets the same "nothing here" as any other address. The database
// checks aal2 itself (private.is_staff), so this screen is the door, not the lock.
//
// Who is staff: the person logged in, not the person being viewed as. While staff view as a member,
// whoami's acting person is the member (with no role) and its staff_role is empty; viewing_as itself
// says the login behind it is an owner or admin, since only they can start one. So /admin shows
// "Stop viewing" then, and opens again once they stop (the critic's fix, stage 5).
//
// Once 2-step login is passed, whoami is read again (loadMe), so me.who.staff_role and everything that
// reads it are current; the Library then loads again for the new key (app/member.jsx).
//
// Supabase Auth's security notification emails are all off (checked by Adam, 1 Oct), so enrolling
// a factor sends nothing, as decision 41 needs.
import { useCallback, useEffect, useState } from 'react'
import { supabase, must } from '../lib/supabase.js'
import { useMember } from '../app/member.jsx'
import { NotFound } from '../shell/Shell.jsx'
import { useViewAs } from '../lib/viewAs.jsx'
import AdminShell, { AdminFrame } from './Shell.jsx'
import { PageHeader, Button, Problem } from './ui/index.js'
import { ROLES } from './shell/routes.js'
import { SITE } from '../config/site.js'

export default function AdminGate() {
  const { me, loadMe } = useMember()
  const [gate, setGate] = useState({ status: 'checking' })
  const viewingAs = me.status === 'ready' && Boolean(me.who?.viewing_as)
  const hasRole = me.status === 'ready' && !viewingAs && ROLES.includes(me.person?.role)

  const check = useCallback(async () => {
    try {
      const { data: aal, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      if (error) throw error
      if (aal.currentLevel === 'aal2') {
        // staff_role comes back only when is_staff() is true, which checks aal2 in the database too
        const who = (await supabase.rpc('whoami').then(must))?.[0]
        if (!who?.staff_role) { setGate({ status: 'closed' }); return }
        // whoami again through the member context, so every screen reads the 2-step role
        if (me.who?.staff_role !== who.staff_role) await loadMe()
        setGate({ status: 'open', role: who.staff_role })
        return
      }
      const { data: factors, error: fErr } = await supabase.auth.mfa.listFactors()
      if (fErr) throw fErr
      const verified = (factors.totp || []).find(x => x.status === 'verified')
      setGate(verified ? { status: 'challenge', factorId: verified.id } : { status: 'enrol' })
    } catch (error) {
      setGate({ status: 'error', error })
    }
  }, [loadMe, me.who?.staff_role])

  const personId = me.status === 'ready' ? me.person.id : null
  useEffect(() => { if (hasRole) check() }, [hasRole, personId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (viewingAs) return <StopViewingFirst />
  if (!hasRole) return <NotFound />
  if (gate.status === 'closed') return <NotFound />
  // the role's own menu only once admin is open; until then, the frame with Member side alone
  if (gate.status === 'open') return <AdminShell role={me.who?.staff_role || gate.role} />
  return (
    <AdminFrame person={me.person}>
      {gate.status === 'checking' && <GateHead title="2-step login" lede="Checking your login." />}
      {gate.status === 'error' && (
        <>
          <GateHead title="2-step login" />
          <Problem onRetry={check}>That didn't load. Check your connection and try again.</Problem>
        </>
      )}
      {gate.status === 'enrol' && <Enrol onDone={check} />}
      {gate.status === 'challenge' && <Challenge factorId={gate.factorId} onDone={check} />}
    </AdminFrame>
  )
}

function GateHead({ title, lede }) {
  return (
    <>
      <PageHeader eyebrow="Admin" title={title} docTitle="Admin" />
      {lede && <p className="ad-lede">{lede}</p>}
    </>
  )
}

// While viewing as a member: admin opens again once they stop
function StopViewingFirst() {
  const { person, stop, busy, error } = useViewAs()
  return (
    <AdminFrame>
      <PageHeader eyebrow="Admin" title={`Viewing as ${person?.full_name || 'a member'}`} docTitle="Admin" />
      <div className="va-gate">
        <p className="ad-lede">Admin opens again once you stop viewing.</p>
        {error && <p className="ad-error" role="alert">{error}</p>}
        <p><Button kind="ink" busy={busy} onClick={() => stop({ to: '/admin' })}>Stop viewing</Button></p>
      </div>
    </AdminFrame>
  )
}

const SIX = /^[0-9]{6}$/

function Enrol({ onDone }) {
  const [factor, setFactor] = useState(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        // clear any half-finished enrolment, so the app being scanned is the 1 that counts. The issuer is
        // the name the authenticator app shows: without it, Supabase used the project's site address
        // (on staging, localhost; Adam, 9 Oct)
        const { data: factors } = await supabase.auth.mfa.listFactors()
        for (const f of (factors?.all || []).filter(x => x.factor_type === 'totp' && x.status !== 'verified')) {
          await supabase.auth.mfa.unenroll({ factorId: f.id })
        }
        const { data, error: err } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'Authenticator app', issuer: SITE.titleSuffix })
        if (err) throw err
        if (alive) setFactor(data)
      } catch {
        if (alive) setError("Setting up 2-step login didn't work. Reload the page to try again.")
      }
    })()
    return () => { alive = false }
  }, [])

  async function submit(e) {
    e.preventDefault()
    if (busy || !factor) return
    if (!SIX.test(code)) { setError('Type the 6-digit code your app shows.'); return }
    setBusy(true)
    setError('')
    const { error: err } = await supabase.auth.mfa.challengeAndVerify({ factorId: factor.id, code })
    setBusy(false)
    if (err) { setError("That code didn't work. Try the newest one your app shows."); return }
    onDone()
  }

  return (
    <>
      <GateHead title="Turn on 2-step login" />
      <div className="ad-mfa">
        <p className="ad-lede">Admin needs a code from an authenticator app as well as your login. Scan this with any authenticator app, such as Google Authenticator or 1Password, then type the 6-digit code it shows.</p>
        {factor && (
          <>
            <img className="ad-mfa-qr" src={factor.totp.qr_code} alt="QR code for your authenticator app" width="200" height="200" />
            <p className="ad-mfa-key">Can't scan it? Type this key into the app: <span className="ad-mfa-secret">{factor.totp.secret}</span></p>
            {/* a second copy of the code, so a lost phone doesn't lock admin away (stage 5's review; undrawn) */}
            <p className="ad-mfa-tip">Scan it on a second device too, such as a password manager, so a lost phone doesn't lock you out.</p>
          </>
        )}
        <CodeForm code={code} setCode={setCode} busy={busy || !factor} error={error} onSubmit={submit} label="Turn it on" />
      </div>
    </>
  )
}

function Challenge({ factorId, onDone }) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function submit(e) {
    e.preventDefault()
    if (busy) return
    if (!SIX.test(code)) { setError('Type the 6-digit code your app shows.'); return }
    setBusy(true)
    setError('')
    const { error: err } = await supabase.auth.mfa.challengeAndVerify({ factorId, code })
    setBusy(false)
    if (err) { setError("That code didn't work. Try the newest one your app shows."); return }
    onDone()
  }

  return (
    <>
      <GateHead title="2-step login" />
      <div className="ad-mfa">
        <p className="ad-lede">Type the 6-digit code from your authenticator app to open admin.</p>
        <CodeForm code={code} setCode={setCode} busy={busy} error={error} onSubmit={submit} label="Open admin" />
      </div>
    </>
  )
}

function CodeForm({ code, setCode, busy, error, onSubmit, label }) {
  return (
    <form className="ad-mfa-form" onSubmit={onSubmit} noValidate>
      <label htmlFor="totp" className="ad-label ad-label--plain">Code from your app</label>
      <input id="totp" className="ad-mfa-code" inputMode="numeric" autoComplete="one-time-code" value={code}
        aria-invalid={error ? true : undefined} aria-describedby={error ? 'totp-error' : undefined}
        onChange={e => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))} />
      {error && <p className="ad-error" id="totp-error" role="alert">{error}</p>}
      <p><Button type="submit" kind="ink" disabled={busy}>{label}</Button></p>
    </form>
  )
}
